import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Parse een D1-timestamp (of ISO 8601). D1 retourneert strings zoals
 * `2026-04-24 10:23:40` of `2026-04-24T10:23:40Z`. Zonder TZ-marker wordt
 * dit als UTC behandeld (want dat is wat SQLite teruggeeft bij CURRENT_TIMESTAMP).
 */
function parseD1Timestamp(s: string): Date {
	// Volledige ISO met Z of +HH:MM - laat JavaScript het regelen
	if (/[Zz]|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
	// "YYYY-MM-DD HH:MM:SS[.sss]" zonder TZ -> forceer UTC
	if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(s)) {
		return new Date(s.replace(' ', 'T') + 'Z');
	}
	return new Date(s);
}

/**
 * Format een datum (zonder tijd) als dd-mm-yyyy.
 * Voor "pure" date-strings (YYYY-MM-DD) wordt zonder timezone-conversie
 * geformat om drift over UTC-grenzen te voorkomen.
 */
export function formatDateISO(iso: string | null | undefined): string {
	if (!iso) return '—';
	const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
	if (dateMatch) {
		const [, yyyy, mm, dd] = dateMatch;
		return `${dd}-${mm}-${yyyy}`;
	}
	try {
		const date = parseD1Timestamp(iso);
		const dd = String(date.getDate()).padStart(2, '0');
		const mm = String(date.getMonth() + 1).padStart(2, '0');
		const yyyy = date.getFullYear();
		return `${dd}-${mm}-${yyyy}`;
	} catch {
		return iso;
	}
}

/**
 * Format een datum+tijd als dd-mm-yyyy HH:MM in de Europe/Amsterdam tijdzone.
 * Werkt ongeacht of de server UTC of offset-met-label teruggeeft.
 */
export function formatDateTimeISO(iso: string | null | undefined): string {
	if (!iso) return '—';
	try {
		const date = parseD1Timestamp(iso);
		const parts = new Intl.DateTimeFormat('nl-NL', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			timeZone: 'Europe/Amsterdam',
		}).formatToParts(date);
		const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
		return `${pick('day')}-${pick('month')}-${pick('year')} ${pick('hour')}:${pick('minute')}`;
	} catch {
		return iso;
	}
}

export function formatAmount(value: string | null | undefined, currency = 'EUR'): string {
	if (!value) return '—';
	const num = Number.parseFloat(value);
	if (Number.isNaN(num)) return value;
	return new Intl.NumberFormat('nl-NL', { style: 'currency', currency }).format(num);
}

export function initials(name: string): string {
	return name
		.split(/\s+/)
		.map((s) => s[0])
		.filter(Boolean)
		.slice(0, 2)
		.join('')
		.toUpperCase();
}
